import React from 'react';
import { Button, Typography, Stack } from '@mui/material';

import { CallMade, Report as ReportIcon } from '@mui/icons-material';

import {
  formatDateTo,
  formatMonetaryAmount,
  useApiGetter,
  SubscriptionService,
  capitalise,
} from '@localstack/services';
import { Invoice, InvoiceStatus as InvoiceStatusType } from '@localstack/types';

import { Divider, GridComponent } from './utils';

export const Invoices = (): JSX.Element => {
  const { data: invoices, isLoading } = useApiGetter(SubscriptionService, 'listInvoices', []);

  if (isLoading) {
    return (
      <GridComponent columns="1fr">
        <Typography color="text.tertiary">Fetching your invoices...</Typography>
      </GridComponent>
    );
  }

  if (invoices === undefined || invoices?.length === 0) {
    return (
      <GridComponent columns="1fr">
        <Typography color="text.tertiary">No invoices yet</Typography>
      </GridComponent>
    );
  }

  return (
    <GridComponent columns="max-content min-content min-content 1fr max-content">
      {invoices?.map((invoice: Invoice, index) => (
        <React.Fragment key={invoice.number}>
          <Typography color="text.secondary">
            <b>{formatDateTo(invoice.status_transitions?.paid_at || invoice.created, 'MMM D, YYYY')}</b>
          </Typography>
          <Typography color="text.secondary">{formatMonetaryAmount(invoice.total, invoice.currency)}</Typography>
          <Typography color="text.secondary">{capitalise(invoice.status)}</Typography>
          <Typography color="text.tertiary">{invoice.number}</Typography>
          {(!!invoice.hosted_invoice_url || !!invoice.invoice_pdf) && (
            <Stack direction="row" alignItems="center" style={{ justifySelf: 'end' }}>
              {invoice.status === InvoiceStatusType.OPEN && invoice.hosted_invoice_url && (
                <>
                  <ReportIcon color="error" fontSize="small" />
                  <Typography
                    color="error"
                    variant="inherit"
                    sx={{ fontSize: '13px', fontWeight: '600', marginRight: '16px', marginLeft: '4px' }}
                  >
                    Payment failed
                  </Typography>
                </>
              )}
              <Button
                onClick={() =>
                  window.open(invoice.hosted_invoice_url || invoice.invoice_pdf || '', '_blank', 'noopener,noreferrer')
                }
                size="small"
                endIcon={<CallMade />}
              >
                {invoice.status === InvoiceStatusType.OPEN && invoice.hosted_invoice_url ? 'Retry' : 'View invoice'}
              </Button>
            </Stack>
          )}
          {index < invoices?.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </GridComponent>
  );
};
